import { BarchartObject, ChartTheme } from './interfaces';

interface ExtendedSeries extends Highcharts.Series {
  total: number;
}

export const getPieChartOptions = (data: { name: string; y: number }[], theme: ChartTheme, totalValue: number) => {
  return {
    chart: {
      type: 'pie',
      custom: {},
      backgroundColor: theme.backgroundColor,
      events: {
        render(this: Highcharts.Chart) {
          const series = this.series[0];
          const seriesData = series as unknown as ExtendedSeries;

          const anyConvert = this.options.chart as { custom: { label: Highcharts.SVGElement } };
          const custom = anyConvert.custom;
          let customLabel = custom.label;

          if (!customLabel) {
            customLabel = custom.label = this.renderer
              .label(`${seriesData.total.toFixed(2)} kwh`, 0)
              .css({
                color: theme.textColor,
                textAnchor: 'middle',
                fontSize: '1.5em',
              })
              .add();
          } else {
            customLabel.attr({
              text: `${seriesData.total.toFixed(2)} kwh`,
            });
          }

          const seriesCenter = (series as unknown as { center: number[] }).center;

          const x = seriesCenter[0] + this.plotLeft,
            y = seriesCenter[1] + this.plotTop - parseFloat(customLabel.attr('height') as string) / 2;

          customLabel.attr({
            x,
            y,
          });
          // Set font size based on chart diameter
          customLabel.css({
            fontSize: `${seriesCenter[2] / 12}px`,
          });
        },
      },
    },
    accessibility: {
      point: {
        valueSuffix: 'kwh',
      },
    },
    title: {
      text: theme.displayText,
      style: {
        color: theme.textColor,
      },
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y} kwh</b>',
      valueDecimals: 2,
    },
    legend: {
      enabled: true,
      itemStyle: {
        color: theme.textColor,
        hover: {
          scale: 2,
        },
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        borderRadius: 8,
        borderWidth: 0,
        showInLegend: true,
        dataLabels: [
          {
            enabled: true,
            distance: 20,
            format: '{point.name}',
          },
          {
            enabled: true,
            distance: -15,
            format: '{point.percentage:.0f}%',
            style: {
              fontSize: '0.9em',
            },
          },
        ],
      },
    },
    exporting: {
      enabled: false,
    },
    series: [
      {
        colorByPoint: true,
        innerSize: '65%',
        data,
        colors: theme.fillColors,
      },
    ],
  };
};

export const barChartOptions = (
  data: BarchartObject,
  theme: ChartTheme,
  showTemperature: boolean,
  temperatureData: number[]
) => {
  return {
    chart: {
      type: 'column',
      backgroundColor: theme.backgroundColor,
    },

    title: {
      text: theme.displayText,
      align: 'center',
      color: theme.textColor,
    },

    xAxis: {
      categories: data.categories,
    },

    yAxis: [
      {
        allowDecimals: false,
        min: 0,
        title: {
          text: 'kWh',
        },
        stackLabels: {
          enabled: false,
          formatter(this: Highcharts.StackItemObject) {
            return `${this.total.toFixed(2)} kwh`;
          },
        },
      },
      ...(showTemperature
        ? [
            {
              // Secondary yAxis
              gridLineWidth: 0,
              allowDecimals: false,
              title: {
                text: 'Temperature',
              },
              labels: {
                format: '{value}°C',
                style: {
                  color: '#7393B3',
                },
              },
              opposite: true,
            },
          ]
        : []),
    ],

    tooltip: {
      format: '<b>{key}</b><br/>{series.name}: {y}<br/>' + `Total: {point.stackTotal.toFixed(2)} kwh`,
      valueDecimals: 2,
    },

    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: false,
          color: '#000000',
          formatter(this: Highcharts.Point) {
            if (!this || !this.y) return '';
            return `${this.y.toFixed(2)} kwh`;
          },
          style: {
            textOutline: '0px',
          },
        },
        pointWidth: data.categories !== undefined ? window.innerWidth / data.categories.length / 2 : 2,
      },
      series: {
        animation: false,
      },
    },
    exporting: {
      enabled: false,
    },

    series: [
      ...data.series.map((seriesItem, index) => ({
        ...seriesItem,
        type: 'column', // Ensures all bar chart data is displayed as a column
        id: `series-${index}`,
        zIndex: 0,
      })),
      ...(showTemperature
        ? [
            {
              name: 'Avg. Outside Temperature',
              data: temperatureData, // Replace with actual temperature data
              type: 'spline',
              yAxis: 1, // Binds this series to the secondary y-axis
                color: '#000000',
                id: 'temperature-series-inside',
                zIndex: 10,
                lineWidth: 1,
                dashStyle: 'Dash', // Make the line dashed
                marker: {
                enabled: temperatureData.length === 1,
                },
            },
          ]
        : []),
    ],
    credits: {
      enabled: true,
      text: showTemperature ? 'Data: DMI, CC BY 4.0' : 'Highcharts.com',
      href: showTemperature ? 'https://creativecommons.org/licenses/by/4.0/' : 'https://www.highcharts.com',
      style: {
        color: '#C0C0C0',
      },
      position: {
        align: 'right',
        x: -10,
        verticalAlign: 'bottom',
        y: -5,
      },
    },
  };
};
